import { useState } from "react";

import { Button } from "@components/Button";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@components/Dialog";
import { Image } from "@components/Image";
import { Stack } from "@components/Stack";
import { Tooltip, TooltipContent, TooltipTrigger } from "@components/Tooltip";
import { Typography } from "@components/Typography";

import { useMuteUserByIdMutation } from "@storage/queries/chat/mute";

import { cn } from "@lib/utils";

import { ICON_PATHS } from "@configs/icons";

enum Term {
	HOUR = "hour",
	DAY = "day",
	WEEK = "week",
	PERMANENT = "permanent",
}
enum MuteTerm {
	HOUR = 60,
	DAY = 1440,
	WEEK = 10080,
}
const termDropdownOption = "z-dropdown cursor-pointer rounded-sm p-3 hover:bg-ground-hover";

interface MuteUserDialogProps {
	userId: string;
	username: string;
	onClose: () => void;
}
export const MuteUserDialog = ({ userId, username, onClose }: MuteUserDialogProps) => {
	const [term, setTerm] = useState("1 hour");
	const [isTermMenuOpen, setIsTermMenuOpen] = useState(false);
	const [muteDuration, setMuteDuration] = useState(MuteTerm.HOUR);

	const muteUserByIdMutation = useMuteUserByIdMutation();

	const closeMuteDialog = (value: boolean) => {
		if (value === false) {
			setIsTermMenuOpen(false);
			setTerm("1 hour");
			onClose();
		}
	};

	const handleMuteUser = async () => {
		muteUserByIdMutation.mutate({ userId, duration: muteDuration });
		closeMuteDialog(false);
		setMuteDuration(MuteTerm.HOUR);
	};

	const selectMuteTerm = (e: React.MouseEvent<HTMLDivElement>, value: number) => {
		setTerm(e.currentTarget.innerText);
		setMuteDuration(value);
	};

	const chevronClass = isTermMenuOpen
		? "duration-150 -translate-y-0.5 rotate-180 [filter:brightness(0)_saturate(100%)_invert(59%)_sepia(7%)_saturate(394%)_hue-rotate(232deg)_brightness(92%)_contrast(87%)]"
		: "duration-150 -translate-y-px transition-transform";

	return (
		<Dialog open onOpenChange={onClose} onOpenChangeComplete={closeMuteDialog}>
			<DialogContent className="max-w-112.5">
				<DialogHeader>
					<DialogTitle>Do you want to mute {username}?</DialogTitle>
				</DialogHeader>

				<Tooltip>
					<TooltipTrigger className="flex h-6 items-center gap-2">
						<Typography color="msg_time" padding="none" size="sm" className="mb-1.5">
							How long would you like to keep the user suspended?
						</Typography>
						<Image src={ICON_PATHS.INFO} alt="info" />
					</TooltipTrigger>
					<TooltipContent className="max-w-50">
						<Typography size="xs" padding="none">
							Removing the user from the Blacklist is possible at any time
						</Typography>
					</TooltipContent>
				</Tooltip>

				<div className="relative" onClick={() => setIsTermMenuOpen((prev) => !prev)}>
					<Stack direction="row" spacing={2} align="center" className="cursor-pointer">
						<Typography size="sm" weight="bold" color={isTermMenuOpen ? "grey-tertiary" : "text"} padding="none">
							{term}
						</Typography>
						<Image className={chevronClass} src={ICON_PATHS.ARROW_DOWN} alt="chevron" />
					</Stack>

					<div
						className={
							isTermMenuOpen
								? `absolute top-5.75 left-0 z-dropdown flex w-56 max-w-49 flex-col justify-center rounded-lg bg-block
									py-1 shadow-xl`
								: "hidden"
						}
					>
						<div
							onClick={(e) => selectMuteTerm(e, MuteTerm.HOUR)}
							className={cn(termDropdownOption, term === "1 hour" ? "bg-primary hover:bg-primary" : "")}
						>
							<Typography size="sm" padding="none">
								1 {Term.HOUR}
							</Typography>
						</div>
						<div
							onClick={(e) => selectMuteTerm(e, MuteTerm.DAY)}
							className={cn(termDropdownOption, term === "1 day" ? "bg-primary hover:bg-primary" : "")}
						>
							<Typography size="sm" padding="none">
								1 {Term.DAY}
							</Typography>
						</div>
						<div
							onClick={(e) => selectMuteTerm(e, MuteTerm.WEEK)}
							className={cn(termDropdownOption, term === "1 week" ? "bg-primary hover:bg-primary" : "")}
						>
							<Typography size="sm" padding="none">
								1 {Term.WEEK}
							</Typography>
						</div>
					</div>
				</div>
				<DialogFooter>
					<Button type="secondary" onClick={() => closeMuteDialog(false)} wide>
						<Typography weight="bold" size="sm" padding="none">
							Cancel
						</Typography>
					</Button>
					<Button onClick={handleMuteUser} wide>
						<Typography weight="bold" size="sm" padding="none">
							Mute
						</Typography>
					</Button>
				</DialogFooter>
			</DialogContent>
		</Dialog>
	);
};
